import { type FormEvent, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useLogin } from "@/api/hooks";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function LoginPage() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { isAuthenticated, isLoading } = useAuth();
	const login = useLogin();

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");

	if (!isLoading && isAuthenticated) {
		const next = searchParams.get("next") ?? "/";
		return <Navigate to={next} replace />;
	}

	const errorMessage =
		login.error instanceof ApiError
			? login.error.detail
			: login.error
				? "Unable to sign in. Please try again."
				: null;

	async function onSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		try {
			await login.mutateAsync({ email, password });
			const next = searchParams.get("next") ?? "/";
			navigate(next, { replace: true });
		} catch {
			// Error surfaced via login.error state; stay on the form.
		}
	}

	return (
		<div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
			<Card className="w-full max-w-sm">
				<CardHeader className="space-y-1">
					<CardTitle className="text-2xl">Veridon Aggregator</CardTitle>
					<CardDescription>Admin dashboard — cross-client view.</CardDescription>
				</CardHeader>
				<CardContent>
					<form className="space-y-4" onSubmit={onSubmit} noValidate>
						<div className="space-y-2">
							<Label htmlFor="email">Email</Label>
							<Input
								id="email"
								type="email"
								autoComplete="username"
								required
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								disabled={login.isPending}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="password">Password</Label>
							<Input
								id="password"
								type="password"
								autoComplete="current-password"
								required
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								disabled={login.isPending}
							/>
						</div>
						{errorMessage ? (
							<p className="text-sm text-destructive" role="alert" aria-live="polite">
								{errorMessage}
							</p>
						) : null}
						<Button type="submit" className="w-full" disabled={login.isPending}>
							{login.isPending ? "Signing in…" : "Sign in"}
						</Button>
					</form>
				</CardContent>
			</Card>
		</div>
	);
}
